import { ArrowLeft, Copy, Pencil, Send, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { MessageDialog } from '../components/ui/alert-dialog-helper';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  formatCurrency,
  getPropertyTypeLabel,
  getQuoteStatusLabel,
  getRoomTypeLabel,
  Quote,
  QuoteRoom,
  QuoteStatus,
} from '../types/quote';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

const QuoteDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  useEffect(() => {
    if (id) loadQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!lightboxUrl) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxUrl(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxUrl]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setQuote(data.data);
      } else {
        setErrorMessage(data.message || 'Failed to load quote');
        setErrorDialogOpen(true);
      }
    } catch {
      setErrorMessage('An error occurred while loading the quote');
      setErrorDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReady = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${id}/mark-ready`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setQuote(data.data);
        setSuccessMessage('Quote marked as ready');
      } else {
        setErrorMessage(data.message || 'Failed to mark quote as ready');
        setErrorDialogOpen(true);
      }
    } catch {
      setErrorMessage('An error occurred');
      setErrorDialogOpen(true);
    }
  };

  const handleSend = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/quotes/${id}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        setQuote(data.data);
        setSuccessMessage('Quote sent to customer');
      } else {
        setErrorMessage(data.message || 'Failed to send quote');
        setErrorDialogOpen(true);
      }
    } catch {
      setErrorMessage('An error occurred');
      setErrorDialogOpen(true);
    }
  };

  const handleCopyShareLink = () => {
    if (quote?.share_token) {
      const link = `${window.location.origin}/quote/${quote.share_token}`;
      navigator.clipboard.writeText(link);
      setSuccessMessage('Share link copied to clipboard');
    }
  };

  const getStatusBadgeVariant = (status: QuoteStatus) => {
    switch (status) {
      case QuoteStatus.ACCEPTED:
      case QuoteStatus.CONVERTED:
        return 'default' as const;
      case QuoteStatus.SENT:
      case QuoteStatus.VIEWED:
        return 'secondary' as const;
      case QuoteStatus.READY:
        return 'warning' as const;
      case QuoteStatus.REJECTED:
      case QuoteStatus.EXPIRED:
        return 'destructive' as const;
      default:
        return 'outline' as const;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="quotes" />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        </main>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="quotes" />
        <main className="container mx-auto px-4 py-8">
          <p className="text-center text-muted-foreground">Quote not found</p>
        </main>
      </div>
    );
  }

  const canEdit = [QuoteStatus.DRAFT, QuoteStatus.READY].includes(quote.status);
  const canMarkReady = quote.status === QuoteStatus.DRAFT;
  const canSend = quote.status === QuoteStatus.READY;

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="quotes" />
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/quotes')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold">{quote.quote_number}</h1>
                <Badge variant={getStatusBadgeVariant(quote.status)}>
                  {getQuoteStatusLabel(quote.status)}
                </Badge>
              </div>
              <p className="text-muted-foreground">
                Created {new Date(quote.created_at).toLocaleDateString()}
                {quote.created_by_user && ` by ${quote.created_by_user.first_name} ${quote.created_by_user.last_name}`}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {quote.share_token && (
              <Button variant="outline" onClick={handleCopyShareLink}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Share Link
              </Button>
            )}
            {canMarkReady && (
              <Button variant="outline" onClick={handleMarkReady}>
                Mark Ready
              </Button>
            )}
            {canSend && (
              <Button variant="outline" onClick={handleSend}>
                <Send className="h-4 w-4 mr-2" />
                Send to Customer
              </Button>
            )}
            {canEdit && (
              <Button onClick={() => navigate(`/quotes/${id}/edit`)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit Quote
              </Button>
            )}
          </div>
        </div>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded-md">
            {successMessage}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Rooms</CardDescription>
              <CardTitle className="text-2xl">{quote.total_rooms_count || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Value</CardDescription>
              <CardTitle className="text-2xl">{formatCurrency(quote.total_price || 0)}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Budget</CardDescription>
              <CardTitle className="text-2xl">{quote.total_budget ? formatCurrency(quote.total_budget) : '-'}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Valid Until</CardDescription>
              <CardTitle className="text-2xl">
                {quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : '-'}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="details">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="rooms">Rooms ({quote.rooms?.length || 0})</TabsTrigger>
            <TabsTrigger value="floor-plans">Floor Plans ({quote.floor_plans?.length || 0})</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{quote.customer_name}</p>
                  </div>
                  {quote.customer_email && (
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p>{quote.customer_email}</p>
                    </div>
                  )}
                  {quote.customer_phone && (
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p>{quote.customer_phone}</p>
                    </div>
                  )}
                  {quote.customer_company && (
                    <div>
                      <p className="text-sm text-muted-foreground">Company</p>
                      <p>{quote.customer_company}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Property Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Property Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {quote.property_name && (
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium">{quote.property_name}</p>
                    </div>
                  )}
                  {quote.property_address && (
                    <div>
                      <p className="text-sm text-muted-foreground">Address</p>
                      <p>{quote.property_address}</p>
                    </div>
                  )}
                  {quote.property_type && (
                    <div>
                      <p className="text-sm text-muted-foreground">Type</p>
                      <p>{getPropertyTypeLabel(quote.property_type)}</p>
                    </div>
                  )}
                  {quote.total_sqft && (
                    <div>
                      <p className="text-sm text-muted-foreground">Size</p>
                      <p>{quote.total_sqft.toLocaleString()} sq ft</p>
                    </div>
                  )}
                  {quote.bedrooms && (
                    <div>
                      <p className="text-sm text-muted-foreground">Bedrooms</p>
                      <p>{quote.bedrooms}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notes */}
              {quote.notes && (
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle>Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap">{quote.notes}</p>
                  </CardContent>
                </Card>
              )}

              {/* Timeline */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-6">
                    <div>
                      <p className="text-sm text-muted-foreground">Created</p>
                      <p>{new Date(quote.created_at).toLocaleString()}</p>
                    </div>
                    {quote.sent_at && (
                      <div>
                        <p className="text-sm text-muted-foreground">Sent</p>
                        <p>{new Date(quote.sent_at).toLocaleString()}</p>
                      </div>
                    )}
                    {quote.viewed_at && (
                      <div>
                        <p className="text-sm text-muted-foreground">Viewed</p>
                        <p>{new Date(quote.viewed_at).toLocaleString()}</p>
                      </div>
                    )}
                    {quote.responded_at && (
                      <div>
                        <p className="text-sm text-muted-foreground">Responded</p>
                        <p>{new Date(quote.responded_at).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="rooms" className="mt-4">
            {quote.rooms && quote.rooms.length > 0 ? (
              <div className="space-y-4">
                {quote.rooms.map((room: QuoteRoom) => {
                  const roomRenderUrl =
                    room.selected_render?.image_url || room.renders?.[0]?.image_url;
                  return (
                  <Card key={room.id}>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">
                            {room.room_name || getRoomTypeLabel(room.room_type)}
                          </CardTitle>
                          <CardDescription>
                            {getRoomTypeLabel(room.room_type)}
                            {room.width_ft && room.length_ft && (
                              <span> • {room.width_ft}' × {room.length_ft}'</span>
                            )}
                            {room.area_sqft && <span> ({room.area_sqft} sq ft)</span>}
                          </CardDescription>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">
                            {room.items_count || 0} items
                          </p>
                          <p className="font-medium">{formatCurrency(room.items_total || 0)}</p>
                          {room.budget && (
                            <p className="text-xs text-muted-foreground">
                              Budget: {formatCurrency(room.budget)}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    {room.items && room.items.length > 0 && (
                      <CardContent>
                        <Separator className="my-3" />
                        <div className="space-y-2">
                          {room.items.map((item) => (
                            <div key={item.id} className="flex justify-between items-center text-sm">
                              <div className="flex items-center gap-2">
                                {item.asset_template?.images?.[0]?.url && (
                                  <img
                                    src={item.asset_template.images[0].url}
                                    alt=""
                                    className="w-8 h-8 object-cover rounded"
                                  />
                                )}
                                <span>
                                  {item.quantity > 1 && `${item.quantity}× `}
                                  {item.asset_template?.name || 'Unknown'}
                                </span>
                              </div>
                              <span className="font-medium">{formatCurrency(item.total_price)}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                    {roomRenderUrl ? (
                        <CardContent className="pt-0">
                          <Separator className="my-3" />
                          <div>
                            <p className="text-sm font-medium mb-2">Room Render</p>
                            <button
                              type="button"
                              className="block w-full max-w-md text-left rounded-lg border overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                              onClick={() => setLightboxUrl(roomRenderUrl)}
                              aria-label="View room render full screen"
                            >
                              <img
                                src={roomRenderUrl}
                                alt={`${room.room_name || room.room_type} render`}
                                className="w-full h-auto object-cover cursor-zoom-in"
                              />
                            </button>
                          </div>
                        </CardContent>
                    ) : null}
                  </Card>
                  );
                })}
              </div>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No rooms added yet.
                  {canEdit && (
                    <Button variant="link" onClick={() => navigate(`/quotes/${id}/edit`)}>
                      Add rooms
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="floor-plans" className="mt-4">
            {quote.floor_plans && quote.floor_plans.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {quote.floor_plans.map((fp) => (
                  <Card key={fp.id}>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">{fp.name || `Floor ${fp.floor_number}`}</CardTitle>
                      <CardDescription>
                        {fp.scale_pixels_per_foot ? 'Scale set' : 'No scale'}
                      </CardDescription>
                    </CardHeader>
                    {fp.image_url && (
                      <CardContent>
                        <img
                          src={fp.image_url}
                          alt={fp.name || `Floor ${fp.floor_number}`}
                          className="w-full h-48 object-contain rounded border"
                        />
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No floor plans uploaded yet.
                  {canEdit && (
                    <Button variant="link" onClick={() => navigate(`/quotes/${id}/edit`)}>
                      Upload floor plans
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
          role="presentation"
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20 z-[101]"
            onClick={() => setLightboxUrl(null)}
            aria-label="Close full screen image"
          >
            <X className="h-6 w-6" />
          </Button>
          <img
            src={lightboxUrl}
            alt="Room render"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
        variant="destructive"
      />
    </div>
  );
};

export default QuoteDetails;
